import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { PII_MASK_CLASS } from "@/lib/analytics";
import type { HttpTypes } from "@medusajs/types";
import { memo, useCallback, useState } from "react";
import { AddressForm } from "./address-form";
import { toAddressInput } from "./address-input";
import type { AddressFormData } from "./address-schema";
import {
  useCreateAddress,
  useUpdateAddress
} from "@/queries/customer/addresses";
import { handleActionError } from "@/lib/errors";

interface AddressDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Address to edit (null for create mode) */
  address?: HttpTypes.StoreCustomerAddress | null;
  /** Whether this is the user's first address (will set both defaults) */
  isFirstAddress?: boolean;
  /** Callback when address is saved successfully */
  onSuccess?: (address?: HttpTypes.StoreCustomerAddress) => void;
}

/**
 * Modal dialog for creating or editing an address
 *
 * Features:
 * - Handles both create and edit modes
 * - Manages form submission with API mutations
 * - Shows loading and error states
 * - Accessible dialog structure
 * - Closes on successful save
 * - Automatically sets both defaults for first address
 */
export const AddressDialog = memo(function AddressDialog({
  open,
  onOpenChange,
  address,
  isFirstAddress = false,
  onSuccess
}: AddressDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);

  const createMutation = useCreateAddress();
  const updateMutation = useUpdateAddress();

  const isEditMode = !!address?.id;
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleClose = useCallback(() => {
    setServerError(null);
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSubmit = useCallback(
    async (data: AddressFormData) => {
      setServerError(null);

      try {
        let savedAddress: HttpTypes.StoreCustomerAddress | undefined;

        if (isEditMode && address?.id) {
          const updateInput: HttpTypes.StoreUpdateCustomerAddress = {
            address_name: data.address_name,
            ...toAddressInput(data)
          };
          const result = await updateMutation.mutateAsync({
            addressId: address.id,
            address: updateInput
          });
          savedAddress = result.customer?.addresses?.find(
            (a) => a.id === address.id
          );
        } else {
          // For the first address, set both default shipping and billing
          // For subsequent addresses, don't set any defaults (user can set them manually)
          const createInput: HttpTypes.StoreCreateCustomerAddress = {
            address_name: data.address_name,
            ...toAddressInput(data),
            is_default_shipping: isFirstAddress,
            is_default_billing: isFirstAddress
          };
          await createMutation.mutateAsync(createInput);
        }

        handleClose();
        onSuccess?.(savedAddress);
      } catch (error) {
        setServerError(
          handleActionError(error, {
            boundary: "address dialog",
            fallback: "We couldn't save this address. Please try again."
          })
        );
      }
    },
    [
      isEditMode,
      address?.id,
      isFirstAddress,
      createMutation,
      updateMutation,
      handleClose,
      onSuccess
    ]
  );

  // Convert address to form default values
  const defaultValues: Partial<AddressFormData> | undefined = address
    ? {
        id: address.id,
        address_name: address.address_name || "",
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        company: address.company || "",
        address_1: address.address_1 || "",
        address_2: address.address_2 || "",
        city: address.city || "",
        postal_code: address.postal_code || "",
        province: address.province || "",
        country_code: address.country_code || "",
        phone: address.phone || ""
      }
    : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        aria-describedby="address-dialog-description"
      >
        <DialogHeader>
          {/* The nickname is salon-authored and often the street itself, so it
              is masked like any other address line (ADR-0006). */}
          <DialogTitle className={isEditMode ? PII_MASK_CLASS : undefined}>
            {isEditMode
              ? `Edit address: ${address?.address_name || "Address"}`
              : "Add new address"}
          </DialogTitle>
          <DialogDescription id="address-dialog-description">
            {isEditMode
              ? "Update the address details below."
              : isFirstAddress
                ? "Enter the details for your first address. It will be set as your default shipping and billing address."
                : "Enter the details for your new address."}
          </DialogDescription>
        </DialogHeader>
        <AddressForm
          key={address?.id || "new"}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          isSubmitting={isSubmitting}
          serverError={serverError}
          submitLabel={isEditMode ? "Save changes" : "Add address"}
        />
      </DialogContent>
    </Dialog>
  );
});
